<template>
    <Modal
        v-if="!redirectTo"
        ref="modalRef"
        :primary-button-disabled="!canSubmit"
        :primary-button-label="trans('assets.quick_onboard.submit')"
        :processing="form.processing"
        :title="trans('assets.quick_onboard.title')"
        max-width="lg"
        @submit="submit"
    >
        <div class="flex flex-col gap-4">
            <section>
                <label class="mb-2 block text-sm font-semibold text-body">
                    {{ trans('assets.quick_onboard.pick_model') }}
                </label>
                <div class="grid grid-cols-1 gap-3 xs:grid-cols-2 lg:grid-cols-3">
                    <button
                        v-for="option in options"
                        :key="option.modelId"
                        type="button"
                        :aria-pressed="form.modelId === option.modelId"
                        class="relative flex min-h-[96px] flex-col items-start gap-1 rounded-xl border-2 p-4 text-left transition-colors focus-visible:outline-none"
                        :class="
                            form.modelId === option.modelId
                                ? 'border-primary bg-primary/5 shadow-[0_0_0_0.15rem] shadow-primary/20'
                                : 'border-panel-border bg-white hover:border-primary/50'
                        "
                        @click="selectModel(option)"
                    >
                        <Badge v-if="option.type" :color="form.modelId === option.modelId ? 'primary' : 'secondary'" class="absolute top-2 right-2">
                            {{ option.type }}
                        </Badge>
                        <div class="text-sm text-gray-text">
                            {{ option.manufacturer?.name || trans('general.not_available') }}
                        </div>
                        <div class="text-base font-semibold text-body">
                            {{ option.designation }}
                        </div>
                    </button>
                </div>
            </section>

            <row v-if="selectedOption">
                <column>
                    <curie-id-scan-input v-model="form.curieId" :error="form.errors.curieId" :required="true" />
                </column>
            </row>

            <row v-if="selectedOption">
                <column>
                    <jet-input
                        v-model="form.serialNumber"
                        :horizontal="false"
                        :invalid-feedback="form.errors.serialNumber"
                        :label="trans('assets.details.serial_number.label')"
                        :placeholder="trans('assets.details.serial_number.placeholder')"
                        :required="true"
                        name="serialNumber"
                    />
                </column>
            </row>

            <section v-if="selectedOption && locationTree">
                <location-form-fields v-model="form.locationId" :location-tree="locationTree" :errors="form.errors.locationId" />
            </section>

            <div class="text-center">
                <button type="button" class="cursor-pointer text-sm font-medium text-primary underline" @click="goToFullForm">
                    {{ trans('assets.quick_onboard.use_full_form') }}
                </button>
            </div>
        </div>
    </Modal>
</template>
<script setup>
import { computed, nextTick, ref } from 'vue';
import { router, useForm } from '@inertiajs/vue3';
import { useModalStack } from '@inertiaui/modal-vue';
import Badge from '@/Components/Badge.vue';
import CurieIdScanInput from '@/Components/CurieIdScanInput.vue';
import JetInput from '@/Jetstream/Input.vue';
import LocationFormFields from '@/Pages/Assets/Edit/LocationFormFields.vue';
import Modal from '@/Components/Modal.vue';
import { trans } from '@/mixins/i18n.js';

const props = defineProps({
    options: {
        type: Array,
        default: () => [],
    },
    locationTree: {
        type: Object,
        default: null,
    },
    redirectTo: {
        type: String,
        default: null,
    },
});

// Reset the modal stack before navigating so the abrupt unmount
// during router.visit() can't leave a dangling stack entry (which
// then hangs the next modal-link click — the loading-spinner bug).
const modalStack = useModalStack();
if (props.redirectTo) {
    nextTick(() => {
        modalStack.reset();
        router.visit(props.redirectTo);
    });
}

const modalRef = ref(null);
const selectedOption = ref(null);

const form = useForm({
    modelId: '',
    locationId: '',
    serialNumber: '',
    curieId: '',
});

const canSubmit = computed(() => !!form.modelId && !!form.locationId && !!form.serialNumber && !!form.curieId);

function selectModel(option) {
    selectedOption.value = option;
    form.modelId = option.modelId;
    // LocationFormFields doesn't emit until interaction; pre-fill from the deepest resolved ancestor.
    const ancestors = props.locationTree?.ancestorsAndSelfLocationIds ?? [];
    const deepest = [...ancestors].reverse().find((id) => !!id);
    if (deepest && ancestors.length > 1) {
        form.locationId = deepest;
    }
}

function submit() {
    if (!canSubmit.value) {
        return;
    }
    form.post(route('assets.quick-onboard.store'), {
        preserveScroll: true,
        onSuccess: () => modalRef.value?.close(),
    });
}

function goToFullForm() {
    modalRef.value?.close();
    router.visit(route('assets.create'));
}
</script>
